/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, EventEmitter, forwardRef, Input, Output } from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator
} from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";

@Component({
	selector: "globalictcc-checkbox",
	standalone: true,
	imports: [MatCheckboxModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CheckboxComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => CheckboxComponent),
			multi: true
		}
	],
	templateUrl: "./checkbox.component.html",
	styleUrl: "./checkbox.component.scss"
})
export class CheckboxComponent implements ControlValueAccessor, Validator {
	@Input() public checked: boolean = false;
	@Input() public disabled: boolean = false;

	@Output() public handleCheckboxChange = new EventEmitter<boolean>();

	public isInvalid: boolean = false;

	/**
	 * Handles the checkbox checked state change.
	 *
	 * @param checked - The new checked state of the checkbox.
	 *
	 * This method updates the internal `checked` property and emits the change event.
	 * It also calls the registered change, touch, and validation callbacks.
	 */
	public onChecked(checked: boolean) {
		this.checked = checked;

		this.handleCheckboxChange.emit(this.checked);

		this._onChange(this.checked);
		this.onTouched();
		this._onValidatorChange();
	}

	/**
	 * Writes a value to the checkbox component.
	 *
	 * @param value - The value to be written to the checkbox.
	 *                If the value is `true`, the checkbox will be checked.
	 *                If the value is `false` or `null`, the checkbox will be unchecked.
	 */
	public writeValue(value: boolean | null) {
		this.checked = !!value;
	}

	/**
	 * Sets the disabled state of the input component.
	 *
	 * @param isDisabled - A boolean value indicating whether the input should be disabled.
	 */
	public setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
	}

	/**
	 * A private method that serves as a placeholder for a change event handler.
	 * This method is intended to be replaced with a custom implementation.
	 *
	 * @param value - The new value of the input, which can be a boolean or null.
	 */
	private _onChange: (value: boolean | null) => void = () => {};

	/**
	 * Registers a callback function that is called when the value changes.
	 *
	 * @param fn - The callback function that is called with the new value.
	 */
	public registerOnChange(fn: (value: boolean | null) => void) {
		this._onChange = fn;
	}

	/**
	 * A callback function that is called when the input component is touched.
	 * This function is typically used in forms to mark the control as "touched".
	 * It is initialized as a no-op function.
	 */
	public onTouched: () => void = () => {};

	/**
	 * Registers a callback function that will be called when the input element is touched.
	 * This method is typically used by Angular forms to register a handler for touch events.
	 *
	 * @param fn - The callback function to be registered.
	 */
	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	/**
	 * A private method that serves as a placeholder for a function that will be called
	 * when the validator changes. This method is initially set to an empty function.
	 *
	 * @private
	 */
	private _onValidatorChange = () => {};

	/**
	 * Registers a function to be called when the validator changes.
	 *
	 * @param fn - The function to be called when the validator changes.
	 */
	public registerOnValidatorChange(fn: () => void) {
		this._onValidatorChange = fn;
	}

	/**
	 * Validates the given form control and updates the `isInvalid` property.
	 *
	 * @param control - The form control to validate.
	 * @returns An object containing validation errors if any, otherwise null.
	 */
	public validate(control: AbstractControl): ValidationErrors | null {
		this.isInvalid = control.invalid && !control.errors && (control.touched || control.dirty);

		return null;
	}
}
